import { config } from '../../config';
import { StorageError } from '../../app/errors';
import type { CountState } from '../../domain/counts';
import type { PositionWrite } from '../../domain/board';
import type { DiscoveryBucket, HypothesisRow, StatusOverride } from '../../types';
import { requireTransaction } from '../commit';
import { db, TABLE } from '../db';

/**
 * The `hypotheses` repo — INV-2, INV-3, INV-4, INV-5.
 *
 * **The load-bearing shape here is the split between two mutators.** `writeCountState` writes
 * `{cachedCount, status, lastIncrementTs, statusOverride}` and *cannot see* `boardPosition`;
 * `writePositions` writes `boardPosition` and *cannot see* a count. That is INV-4 — *"`boardPosition`
 * … is never changed by increments, status changes, or Discovery visits"* — expressed as two
 * disjoint parameter types rather than as a rule someone has to remember while editing a handler.
 * It is the storage-layer mirror of design §4's compile-time patch key unions, and it also proves
 * LOCK-5: a Discovery visit dispatches the same increment as the board, and neither can move a tile.
 *
 * `status` is never a parameter to anything here. It arrives inside a `CountState`, which
 * `domain/counts.ts` can only build through `deriveStatus` — the one expression at the one call site
 * design §3.3 requires. No function in this module accepts a bare `status`.
 */

/**
 * Inserts a new hypothesis.
 *
 * `add`, not `put`: a colliding id means two different hypotheses share an identity, which must
 * surface rather than silently overwrite one of them.
 *
 * @throws {StorageError} outside a transaction, on a duplicate id, on a `boardPosition` outside
 *   `[0, BOARD_CAP)`, or on a write failure. A duplicate `[matchId+boardPosition]` surfaces from the
 *   unique index as an aborted transaction (INV-5).
 */
export async function insertHypothesis(row: HypothesisRow): Promise<void> {
  requireTransaction('insertHypothesis');
  assertPositionInRange(row.boardPosition, row.id);
  try {
    await db().hypotheses.add(row);
  } catch (cause) {
    throw new StorageError('Could not create the hypothesis.', {
      cause,
      context: { store: TABLE.hypotheses, id: row.id, matchId: row.matchId },
    });
  }
  await assertBoardCap(row.matchId);
}

/**
 * Writes the four count-derived fields, and only those (INV-2, INV-3).
 *
 * Called in the same transaction as the corresponding Observation insert or delete, with `next`
 * computed by `domain/counts.ts` from a row read **inside** that transaction — which is what makes
 * INV-2's "`cachedCount` equals the count of its `increment` observations at all times after any
 * transaction commits" true without a read-then-write race under the single-flight queue (§1.1).
 *
 * @throws {StorageError} outside a transaction, if the row is missing, or on a write failure.
 */
export async function writeCountState(id: string, next: CountState): Promise<void> {
  requireTransaction('writeCountState');
  const table = db().hypotheses;
  const row = await table.get(id);
  if (!row) {
    throw new StorageError(`Hypothesis ${id} no longer exists; the count was not written.`, {
      context: { store: TABLE.hypotheses, id },
    });
  }
  try {
    await table.put({
      ...row,
      cachedCount: next.cachedCount,
      lastIncrementTs: next.lastIncrementTs,
      statusOverride: next.statusOverride,
      status: next.status,
    });
  } catch (cause) {
    throw new StorageError('Could not write the hypothesis count.', {
      cause,
      context: { store: TABLE.hypotheses, id },
    });
  }
}

/**
 * The **only** writer of `boardPosition` (INV-4, INV-5, LOCK-7).
 *
 * Writes are applied **in the order given**. That ordering is part of the plan
 * (`domain/board.planSwap` nulls the outgoing slot first), because IndexedDB enforces uniqueness per
 * `put`: a swap that writes A→slot 1 while B still holds slot 1 throws `ConstraintError` even inside
 * one transaction (§3.4). Reordering these writes for tidiness breaks every board swap.
 *
 * After applying, the positioned rows for the match are counted and the transaction **aborts** above
 * `BOARD_CAP`. That is INV-5's in-transaction postcondition, placed here rather than left to the
 * caller: an aborted transaction commits nothing, so no acknowledgment renders (SES-2) and no 9th
 * tile is ever observed (LIVE-8) — on every path, including an import.
 *
 * @throws {StorageError} outside a transaction, on a position outside `[0, BOARD_CAP)`, on a missing
 *   row, above `BOARD_CAP` positioned rows, or on a write failure.
 */
export async function writePositions(
  matchId: string,
  writes: readonly PositionWrite[],
): Promise<void> {
  requireTransaction('writePositions');
  if (writes.length === 0) return;
  const table = db().hypotheses;

  for (const write of writes) assertPositionInRange(write.boardPosition, write.id);

  for (const write of writes) {
    const row = await table.get(write.id);
    if (!row) {
      throw new StorageError(
        `Hypothesis ${write.id} no longer exists; the board change was rolled back.`,
        { context: { store: TABLE.hypotheses, id: write.id, matchId } },
      );
    }
    if (row.matchId !== matchId) {
      throw new StorageError(
        `Hypothesis ${write.id} belongs to a different match; a board change may not reach across matches.`,
        { context: { store: TABLE.hypotheses, id: write.id, rowMatchId: row.matchId, matchId } },
      );
    }
    try {
      await table.put({ ...row, boardPosition: write.boardPosition });
    } catch (cause) {
      throw new StorageError(
        `Could not write board position ${String(write.boardPosition)}. If another tile already holds it, the plan must clear that slot first.`,
        { cause, context: { store: TABLE.hypotheses, ...write, matchId } },
      );
    }
  }

  await assertBoardCap(matchId);
}

/** INV-8's deletion path. Named for its single caller so it cannot be mistaken for a general one. */
export async function deleteHypothesesForCascade(ids: readonly string[]): Promise<void> {
  requireTransaction('deleteHypothesesForCascade');
  if (ids.length === 0) return;
  try {
    await db().hypotheses.bulkDelete([...ids]);
  } catch (cause) {
    throw new StorageError('Could not delete the match’s hypotheses during a cascade.', {
      cause,
      context: { store: TABLE.hypotheses, count: ids.length },
    });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Reads
// ─────────────────────────────────────────────────────────────────────────────

/** @throws {StorageError} on a read failure. */
export async function getHypothesis(id: string): Promise<HypothesisRow | undefined> {
  try {
    return await db().hypotheses.get(id);
  } catch (cause) {
    throw new StorageError('Could not read the hypothesis.', {
      cause,
      context: { store: TABLE.hypotheses, id },
    });
  }
}

/** Every hypothesis of a match, positioned or list-only. @throws {StorageError} */
export async function listHypothesesForMatch(matchId: string): Promise<HypothesisRow[]> {
  try {
    return await db().hypotheses.where('matchId').equals(matchId).toArray();
  } catch (cause) {
    throw new StorageError('Could not read the match’s hypotheses.', {
      cause,
      context: { store: TABLE.hypotheses, matchId },
    });
  }
}

/**
 * The board's rows — those holding a position.
 *
 * Read from `&[matchId+boardPosition]`. A compound index entry with a `null` component is **omitted**
 * from an IndexedDB index (§3.4), so this range returns exactly the positioned rows and nothing has
 * to filter nulls. That omission is also what leaves list-only rows correctly unconstrained by the
 * uniqueness. Do not "fix" it.
 *
 * Returned in position order; `domain/order.lockedOrder` decides the *rendered* order, which is a
 * different thing (INV-11) and is not this repo's business.
 *
 * @throws {StorageError} on a read failure.
 */
export async function listPositioned(matchId: string): Promise<HypothesisRow[]> {
  try {
    return await db()
      .hypotheses.where('[matchId+boardPosition]')
      .between([matchId, -Infinity], [matchId, Infinity])
      .toArray();
  } catch (cause) {
    throw new StorageError('Could not read the board.', {
      cause,
      context: { store: TABLE.hypotheses, matchId },
    });
  }
}

/** INV-5's count. @throws {StorageError} on a read failure. */
export async function countPositioned(matchId: string): Promise<number> {
  try {
    return await db()
      .hypotheses.where('[matchId+boardPosition]')
      .between([matchId, -Infinity], [matchId, Infinity])
      .count();
  } catch (cause) {
    throw new StorageError('Could not count the board’s tiles.', {
      cause,
      context: { store: TABLE.hypotheses, matchId },
    });
  }
}

/**
 * DISC-2's page contents.
 *
 * Filtered in memory from the match's rows rather than through a `[matchId+discoveryBucket]` index:
 * `hypotheses` sits inside the design §5.1 tap transaction, every index on it is maintained on every
 * increment, and build unit U-0 measured A-1 against exactly the §3.4 index set. A fourth index
 * bought for a four-way filter over a few dozen rows would change the number the whole design rests
 * on. If Discovery ever needs it, that is a decision to re-run U-0.
 *
 * @throws {StorageError} on a read failure.
 */
export async function listByDiscoveryBucket(
  matchId: string,
  bucket: DiscoveryBucket,
): Promise<HypothesisRow[]> {
  return (await listHypothesesForMatch(matchId)).filter((row) => row.discoveryBucket === bucket);
}

/**
 * Rows carrying a dismissal, read from `[matchId+statusOverride]`.
 *
 * `null` overrides are **absent** from the compound index for the same reason positions are, so this
 * returns only dismissed rows — which is what HT-5 and DIST-2 want to exclude.
 *
 * @throws {StorageError} on a read failure.
 */
export async function listDismissed(
  matchId: string,
  override: Exclude<StatusOverride, null>,
): Promise<HypothesisRow[]> {
  try {
    return await db()
      .hypotheses.where('[matchId+statusOverride]')
      .equals([matchId, override])
      .toArray();
  } catch (cause) {
    throw new StorageError('Could not read the dismissed hypotheses.', {
      cause,
      context: { store: TABLE.hypotheses, matchId, override },
    });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Guards
// ─────────────────────────────────────────────────────────────────────────────

function assertPositionInRange(boardPosition: number | null, id: string): void {
  if (boardPosition === null) return;
  if (!Number.isInteger(boardPosition) || boardPosition < 0 || boardPosition >= config.BOARD_CAP) {
    throw new StorageError(
      `INV-5: boardPosition must be an integer in [0, ${config.BOARD_CAP}); got ${String(boardPosition)}.`,
      { context: { store: TABLE.hypotheses, id, boardPosition } },
    );
  }
}

/**
 * INV-5's in-transaction postcondition. Throwing aborts the transaction, so nothing commits.
 *
 * The unique index alone cannot enforce the cap — it forbids two tiles in slot 3, not nine tiles in
 * slots 0–8. This is the other half.
 */
async function assertBoardCap(matchId: string): Promise<void> {
  const filled = await countPositioned(matchId);
  if (filled > config.BOARD_CAP) {
    throw new StorageError(
      `INV-5: the board would hold ${filled} tiles, above BOARD_CAP of ${config.BOARD_CAP}. Nothing was committed.`,
      { context: { store: TABLE.hypotheses, matchId, filled, cap: config.BOARD_CAP } },
    );
  }
}
